// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { RecruitLookLogo } from '@/components/ui/logo';
import { Eye, EyeOff } from 'lucide-react';
import { NO_ACCESS_LANDING_PATH } from '@/lib/rolePaths';
import { resolveRleventsAccess } from '@/lib/rleventsAccess';
import { getSessionUser } from '@/lib/authSession';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get('email') ?? '';
  const redirectTo = searchParams.get('redirect');
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn } = useAuth();

  useEffect(() => {
    if (prefilledEmail) setEmail(prefilledEmail);
  }, [prefilledEmail]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await signIn(email, password);

      if (!result.success) {
        toast({
          title: "Login failed",
          description: result.error || 'Sign-in failed',
          variant: "destructive",
        });
      } else {
        toast({
          title: "Welcome back!",
          description: "You have successfully logged in.",
        });

        // An explicit redirect (e.g. returning to an invitation) wins over the
        // role-based landing path. Otherwise RLevents routes admins and staff;
        // everyone else lands on the "account not set up" page — no role guessing.
        if (redirectTo && redirectTo.startsWith('/')) {
          navigate(redirectTo, { replace: true });
          return;
        }
        const { data: { user: signedInUser } } = await getSessionUser();
        const userId = signedInUser?.id;
        let target = '/dashboard';
        if (userId) {
          const access = await resolveRleventsAccess(userId);
          // Unknown permissions go to /dashboard, which retries and shows a
          // banner — never to "account not set up".
          target = access.status === 'ok' ? access.landingPath : '/dashboard';
        }
        navigate(target, { replace: true });
      }
    } catch (error) {
      toast({
        title: "An error occurred",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center space-y-4">
            <Link to="/" className="flex justify-center">
              <RecruitLookLogo size="xl" />
            </Link>
            <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
            <CardDescription>
              Sign in to your account to continue your recruiting journey
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="athlete@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  readOnly={!!prefilledEmail}
                  className="h-12"
                />
                {prefilledEmail && (
                  <p className="text-xs text-muted-foreground">
                    This email is set by your invitation link.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link 
                    to="/auth/forgot-password" 
                    className="text-sm text-secondary hover:text-secondary/80 font-medium"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 bg-secondary hover:bg-secondary/90 text-slate-900 font-black text-lg"
                disabled={isLoading}
              >
                {isLoading ? "Signing In..." : "SIGN IN"}
              </Button>
            </form>
            
            <div className="mt-6 text-center space-y-4">
              <p className="text-xs text-gray-500">
                Accounts are created by invitation only. Contact your administrator for access.
              </p>
              <Link to="/" className="text-sm text-gray-600 hover:text-secondary block">
                ← Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}